import React, { useState } from 'react';
import { Alert, Box, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import { EventClickArg } from '@fullcalendar/core';
import {
  AccessibleVisitationTeam,
  GetVisitationTeamInsightsResponse,
  VisitationAgendaCaseModel,
  VisitationAgendaModel,
  VisitationDecision,
  VisitationMeetingModel,
} from '../../api-service/clients';
import { FasitCalendarWrapper } from '../../components/calendar/fasit-calendar-wrapper';
import { CircularProgressCentered } from '../../components/circular-progress-centered/circular-progress-centered';
import { VisitationAssessmentDraft } from './visitation-assessment-draft';
import { mapVisitationMeetingEvents, VisitationMeetingEventProps } from './visitation-meeting-events';
import { VisitationAgendaComponent } from './visitation-agenda-component';
import { VisitationTeamInsightsComponent } from './visitation-team-insights-component';

export interface VisitationTeamPageComponentProps {
  teams: AccessibleVisitationTeam[];
  selectedTeamId: string | undefined;
  meetings: VisitationMeetingModel[];
  selectedMeetingKey: string | undefined;
  agenda: VisitationAgendaModel | undefined;
  drafts: Record<string, VisitationAssessmentDraft>;
  insights: GetVisitationTeamInsightsResponse | undefined;
  isLoadingInsights: boolean;
  isLoadingTeams: boolean;
  isLoadingMeetings: boolean;
  isLoadingAgenda: boolean;
  isSaving: boolean;
  onSelectTeam: (visitationTeamId: string) => void;
  onVisibleRangeChange: (range: { start: Date; end: Date }) => void;
  onSelectMeeting: (meeting: VisitationMeetingModel) => void;
  onCloseAgenda: () => void;
  onReopenAgenda: () => void;
  onCompressAgenda: () => void;
  onCancelMeeting: () => void;
  onGrantDecisionChange: (visitationBookingId: string, grantId: string, decision: VisitationDecision) => void;
  onGrantCommentChange: (visitationBookingId: string, grantId: string, comment: string) => void;
  onReasoningChange: (visitationBookingId: string, reasoning: string) => void;
  onProcess: (agendaCase: VisitationAgendaCaseModel) => void;
  onPostpone: (agendaCase: VisitationAgendaCaseModel) => void;
}

export function VisitationTeamPageComponent(props: VisitationTeamPageComponentProps) {
  const { teams, selectedTeamId, meetings, selectedMeetingKey, agenda, isLoadingTeams, isLoadingMeetings, isLoadingAgenda, onSelectTeam, onVisibleRangeChange, onSelectMeeting } =
    props;
  const [activeTab, setActiveTab] = useState<'agenda' | 'insights'>('agenda');

  if (isLoadingTeams) {
    return <CircularProgressCentered />;
  }

  if (teams.length === 0) {
    return (
      <Box>
        <Alert severity="info">Du er ikke medlem af et visitationsteam.</Alert>
      </Box>
    );
  }

  function handleTeamChange(event: React.ChangeEvent<HTMLInputElement>) {
    onSelectTeam(event.target.value);
  }

  function handleEventClick(click: EventClickArg) {
    onSelectMeeting((click.event.extendedProps as VisitationMeetingEventProps).meeting);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Visitationsteam
        </Typography>
        {teams.length > 1 && (
          <TextField select label="Team" value={selectedTeamId ?? ''} onChange={handleTeamChange} variant="filled" sx={{ minWidth: 280 }}>
            {teams.map(team => (
              <MenuItem key={team.visitationTeamId} value={team.visitationTeamId}>
                {team.name}
                {team.isActive ? '' : ' (inaktiv)'}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      <Tabs value={activeTab} onChange={(_, tab) => setActiveTab(tab)} sx={{ mb: 2 }}>
        <Tab value="agenda" label="Møder og dagsorden" />
        <Tab value="insights" label="Styring" />
      </Tabs>
      {activeTab === 'insights' && <VisitationTeamInsightsComponent insights={props.insights} isLoading={props.isLoadingInsights} />}
      <Box sx={{ display: activeTab === 'agenda' ? 'grid' : 'none', gridTemplateColumns: { xs: '1fr', xl: 'minmax(520px, 2fr) 3fr' }, gap: 3, alignItems: 'start' }}>
        <FasitCalendarWrapper
          key={selectedTeamId}
          toolbarVariant="compact"
          events={mapVisitationMeetingEvents(meetings, selectedMeetingKey)}
          eventClick={handleEventClick}
          onVisibleRangeChange={onVisibleRangeChange}
          isLoading={isLoadingMeetings}
          slotMinTime="07:00:00"
          slotMaxTime="18:00:00"
          allDaySlot={false}
        />
        <Box>
          {isLoadingAgenda && <CircularProgressCentered />}
          {!isLoadingAgenda && !agenda && <Alert severity="info">Vælg et møde i kalenderen for at se dagsordenen.</Alert>}
          {!isLoadingAgenda && agenda && <VisitationAgendaComponent {...props} agenda={agenda} />}
        </Box>
      </Box>
    </Box>
  );
}
